import { FC, useState, useMemo } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Checkbox,
    Box,
    Skeleton,
} from '@mui/material';
import { DataTableProps, TableColumn } from './types/TableTypes';
import { DataTableHeader } from './components/DataTableHeader';
import { DataTableEmpty } from './components/DataTableEmpty';
import { DataTableActions } from './components/DataTableActions';
import { DataTableFilters } from './components/DataTableFilters';
import { getFormattedDate } from '../../shared/util/dateHelper';

import './table.css';

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator<T>(
    order: Order,
    orderBy: keyof T
): (a: T, b: T) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// Search function - searches across all string fields
function searchData<T>(data: T[], searchValue: string, columns: TableColumn<T>[]): T[] {
    if (!searchValue) return data;

    const lowerSearch = searchValue.toLowerCase();

    return data.filter((row) => {
        return columns.some((column) => {
            if (column.field === 'actions') return false; // Skip actions column

            const value = row[column.field as keyof T];
            if (value === null || value === undefined) return false;

            const stringValue = String(value).toLowerCase();
            return stringValue.includes(lowerSearch);
        });
    });
}

// Filter function - applies all active filters
function filterData<T>(data: T[], filters: Record<string, any>): T[] {
    let filtered = data;

    Object.entries(filters).forEach(([field, value]) => {
        if (value === '' || value === undefined || value === null) return;

        filtered = filtered.filter((row: any) => {
            const rowValue = row[field];

            // Handle date range filtering
            if (typeof value === 'object' && 'from' in value && 'to' in value) {
                if (!value.from && !value.to) return true;

                const rowDate = rowValue ? new Date(rowValue) : null;
                if (!rowDate || isNaN(rowDate.getTime())) return false;

                const fromDate = value.from ? new Date(value.from) : null;
                const toDate = value.to ? new Date(value.to) : null;

                if (fromDate && toDate) {
                    // Set time to start and end of day for accurate comparison
                    fromDate.setHours(0, 0, 0, 0);
                    toDate.setHours(23, 59, 59, 999);
                    return rowDate >= fromDate && rowDate <= toDate;
                } else if (fromDate) {
                    fromDate.setHours(0, 0, 0, 0);
                    return rowDate >= fromDate;
                } else if (toDate) {
                    toDate.setHours(23, 59, 59, 999);
                    return rowDate <= toDate;
                }

                return true;
            }

            // Handle different value types
            if (typeof value === 'boolean') {
                return rowValue === value;
            }

            return String(rowValue) === String(value);
        });
    });

    return filtered;
}

const DataTable: FC<DataTableProps> = ({
    title,
    icon,
    columns,
    data,
    actions,
    loading = false,
    emptyMessage = 'No Data Found',
    emptyDescription,
    pagination = true,
    pageSize = 10,
    sortable = true,
    selectable = false,
    dense = true,
    stickyHeader = true,
    maxHeight,
    onRowClick,
    onSelectionChange,
    searchable = false,
    searchPlaceholder = 'Search...',
    filters,
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize);
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<any>(columns[0]?.field);
    const [selected, setSelected] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

    const handleRequestSort = (property: any) => {
        if (!sortable) return;

        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelected(data);
            onSelectionChange?.(data);
        } else {
            setSelected([]);
            onSelectionChange?.([]);
        }
    };

    const handleSelect = (row: any) => {
        const selectedIndex = selected.indexOf(row);
        let newSelected;

        if (selectedIndex === -1) {
            newSelected = [...selected, row];
        } else {
            newSelected = selected.filter((item) => item !== row);
        }

        setSelected(newSelected);
        onSelectionChange?.(newSelected);
    };

    const isSelected = (row: any) => selected.indexOf(row) !== -1;

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (field: string, value: any) => {
        setActiveFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
        setPage(0); // Reset to first page when filter changes
    };

    const handleClearFilters = () => {
        setActiveFilters({});
        setSearchValue('');
        setPage(0);
    };

    // Filter, search, sort, and paginate data
    const processedData = useMemo(() => {
        // 1. Apply filters
        let filtered = filterData(data, activeFilters);

        // 2. Apply search
        filtered = searchData(filtered, searchValue, columns);

        // 3. Sort
        if (sortable) {
            filtered = [...filtered].sort(getComparator(order, orderBy));
        }

        return filtered;
    }, [data, activeFilters, searchValue, order, orderBy, sortable, columns]);

    const paginatedData = useMemo(() => {
        if (!pagination) return processedData;
        return processedData.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );
    }, [processedData, page, rowsPerPage, pagination]);

    const renderCellValue = (column: TableColumn, row: any) => {
        const value = row[column.field];

        if (column.renderCell) {
            return column.renderCell(value, row);
        }

        if (column.field === 'actions' && actions) {
            return <DataTableActions row={row} actions={actions} />;
        }

        if (column.field === 'createAt' || column.field === 'createdAt') {
            return getFormattedDate(value);
        }

        return value?.toString() || '-';
    };

    if (loading) {
        return (
            <Box className="iauto-data-table-wrapper">
                <DataTableHeader title={title} icon={icon} />
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer>
                        {[...Array(5)].map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rectangular"
                                height={60}
                                sx={{ mb: 1 }}
                            />
                        ))}
                    </TableContainer>
                </Paper>
            </Box>
        );
    }

    // Empty state
    if (!data || data.length === 0) {
        return (
            <Box className="iauto-data-table-wrapper">
                <DataTableHeader title={title} icon={icon} />
                <DataTableEmpty
                    message={emptyMessage}
                    description={emptyDescription || `No data was found for: ${title}`}
                />
            </Box>
        );
    }

    return (
        <Box className="iauto-data-table-wrapper">
            <DataTableHeader title={title} icon={icon} />

            {/* Search and Filters */}
            {(searchable || (filters && filters.length > 0)) && (
                <DataTableFilters
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    filters={filters}
                    activeFilters={activeFilters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    placeholder={searchPlaceholder}
                />
            )}

            <Paper
                sx={{
                    width: '100%',
                    overflow: 'hidden',
                    borderRadius: 2,
                }}
                elevation={2}
            >
                <TableContainer
                    sx={{
                        maxHeight: maxHeight || 'auto',
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    }}
                >
                    <Table
                        stickyHeader={stickyHeader}
                        size={dense ? 'small' : 'medium'}
                        sx={{
                            minWidth: 650,
                            '& .MuiTableCell-root': {
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': {
                                    borderRight: 'none',
                                },
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {selectable && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={
                                                selected.length > 0 &&
                                                selected.length < data.length
                                            }
                                            checked={
                                                data.length > 0 &&
                                                selected.length === data.length
                                            }
                                            onChange={handleSelectAll}
                                            color="primary"
                                        />
                                    </TableCell>
                                )}
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.field as string}
                                        align={column.align || 'left'}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.813rem',
                                            backgroundColor: 'primary.light',
                                            color: 'primary.contrastText',
                                            minWidth: column.minWidth,
                                            width: column.width,
                                            flex: column.flex,
                                            py: 1,
                                            px: 2,
                                        }}
                                    >
                                        {sortable && column.sortable !== false ? (
                                            <TableSortLabel
                                                active={orderBy === column.field}
                                                direction={
                                                    orderBy === column.field
                                                        ? order
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleRequestSort(column.field)
                                                }
                                                sx={{
                                                    color: 'inherit !important',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                {column.headerName}
                                            </TableSortLabel>
                                        ) : (
                                            column.headerName
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((row, rowIndex) => {
                                const isItemSelected = isSelected(row);

                                return (
                                    <TableRow
                                        key={rowIndex}
                                        hover
                                        selected={isItemSelected}
                                        onClick={() => {
                                            if (onRowClick) {
                                                onRowClick(row);
                                            } else if (selectable) {
                                                handleSelect(row);
                                            }
                                        }}
                                        sx={{
                                            cursor: onRowClick || selectable ? 'pointer' : 'default',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        }}
                                    >
                                        {selectable && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isItemSelected}
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelect(row);
                                                    }}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.field as string}
                                                align={column.align || 'left'}
                                                sx={{
                                                    py: dense ? 0.75 : 2,
                                                    px: 2,
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                {renderCellValue(column, row)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={processedData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default DataTable;

